import Button from "@shared/v2/Button";
import Tab from "@shared/v2/Tab";
import TabBar from "@shared/v2/TabBar";
import Typography from "@shared/v2/Typography";
import { useRef, useState } from "react";
import { SwitchTransition, Transition } from "react-transition-group";
import PaymentMethods from "./PaymentMethods";
import Plans from "./Plans";
import getTransitionStyles from "./utils/getTransitionStyles";
import usePortalUrl from "./hooks/usePortalUrl";
import { useCurrentUser } from "../reducers/layoutReducer/selectors";

enum AddOnsBillingTabs {
  PlansAndPaymentHistory = "Plans & Payment History",
  PaymentMethods = "Payment Methods",
}

const TABS = {
  [AddOnsBillingTabs.PlansAndPaymentHistory]: Plans,
  [AddOnsBillingTabs.PaymentMethods]: PaymentMethods,
};

const AddOnsBilling = () => {
  const {
    account: { isLever },
  } = useCurrentUser() as { account: { isLever: boolean } };
  const [tab, setTab] = useState(
    isLever ? AddOnsBillingTabs.PaymentMethods : AddOnsBillingTabs.PlansAndPaymentHistory,
  );
  const directionRef = useRef<"left" | "right">("right");
  const tabRefs = {
    [AddOnsBillingTabs.PlansAndPaymentHistory]: useRef<HTMLDivElement | null>(null),
    [AddOnsBillingTabs.PaymentMethods]: useRef<HTMLDivElement | null>(null),
  };
  const portalUrl = usePortalUrl();

  const handleTabChange = (newTab: AddOnsBillingTabs) => {
    if (newTab === tab) return;
    const AddOnsBillingTabsValues = Object.values(AddOnsBillingTabs);
    const currentIndex = AddOnsBillingTabsValues.indexOf(tab);
    const newIndex = AddOnsBillingTabsValues.indexOf(newTab);
    const isMovingRight = newIndex > currentIndex;
    directionRef.current = isMovingRight ? "right" : "left";
    setTab(newTab);
  };

  const ActiveTab = TABS[tab];

  return (
    <div className="tw-flex tw-flex-col tw-px-[32px] tw-py-[24px] tw-gap-[16px] tw-max-w-[1300px] tw-mx-auto tw-h-full tw-overflow-y-auto">
      <div className="tw-bg-white tw-rounded-[12px] tw-p-[32px] tw-border-solid tw-border-[1px] tw-border-gray-10 tw-flex tw-flex-wrap tw-justify-between tw-gap-[16px]">
        <div className="tw-flex tw-flex-col tw-gap-[16px] tw-max-w-[600px]">
          {/* @ts-expect-error props */}
          <Typography variant="headline1">Add-Ons Billing</Typography>
          {/* @ts-expect-error props */}
          <Typography>
            Manage your payment methods, manage your add-ons plans, and view your payment history. To make
            changes to your CRM, please reach out to support.
          </Typography>
        </div>
        {/* @ts-expect-error props */}
        <Button
          className="tw-text-nowrap"
          schema="secondary"
          size="medium"
          onClick={() => window.location.assign("/add_ons")}
        >
          View Add-Ons
        </Button>
      </div>
      <div className="tw-flex-none tw-bg-white tw-rounded-[12px] tw-px-[32px] tw-pb-[32px] tw-pt-[16px] tw-border-solid tw-border-[1px] tw-border-gray-10 tw-flex tw-flex-col tw-gap-[32px] tw-overflow-hidden">
        <TabBar containerClassName="tw-w-full">
          {!isLever && (
            // @ts-expect-error props
            <Tab
              selected={tab === AddOnsBillingTabs.PlansAndPaymentHistory}
              label={AddOnsBillingTabs.PlansAndPaymentHistory}
              onClick={() => handleTabChange(AddOnsBillingTabs.PlansAndPaymentHistory)}
            />
          )}
          {/* @ts-expect-error props */}
          <Tab
            selected={tab === AddOnsBillingTabs.PaymentMethods}
            label={AddOnsBillingTabs.PaymentMethods}
            onClick={() => handleTabChange(AddOnsBillingTabs.PaymentMethods)}
          />
        </TabBar>
        <SwitchTransition mode="out-in">
          <Transition
            key={tab}
            nodeRef={tabRefs[tab]}
            timeout={200}
            onEnter={() =>
              // Force reflow before entering transition starts
              tabRefs[tab].current?.offsetHeight
            }
          >
            {(state) => (
              <div ref={tabRefs[tab]} style={getTransitionStyles(state, directionRef.current)}>
                <ActiveTab portalUrl={portalUrl} />
              </div>
            )}
          </Transition>
        </SwitchTransition>
      </div>
    </div>
  );
};
export default AddOnsBilling;
